import { useEffect } from "react";
import useSendDataToGTM from "@features/analytics/hooks/useSendDataToGTM";
import { useUserInfo } from "@features/mySph/hooks/useUserInfo";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useIsFirstMount } from "@hooks/useIsFirstMount";
import { useGetReadingHistory } from "@routes/MyAccount/hooks/useGetReadingHistory";

import MyAccountRecentReadPopoverEmptyState from "./MyAccountReadingHistoryPopoverEmptyState";
import MyAccountRecentReadPopoverList from "./MyAccountReadingHistoryPopoverList";

/**
 * MyAccountReadingHistoryPopover Component - Popover for recent reading history
 */
const MyAccountReadingHistoryPopover = (): React.ReactElement => {
  const { visitorId } = useUserInfo();

  const sendDataToGTM = useSendDataToGTM();
  const isFirstMount = useIsFirstMount();

  const { readArticleList, isLoading } = useGetReadingHistory({
    pageSize: 6,
  });

  const isEmptyState = readArticleList.length === 0;
  const isDisplayViewMore = readArticleList.length > 5;

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!isFirstMount) return;

    sendDataToGTM({
      event: "custom_event",
      eventCategory: "recent read",
      eventAction: "visible",
      eventLabel: window.location.href,
    });
  }, [sendDataToGTM, isFirstMount]);

  return (
    <div
      data-testid="my-account-reading-history-popover-component"
      className="w-[360px] p-6"
    >
      {!visitorId || (isEmptyState && !isLoading) ? (
        <MyAccountRecentReadPopoverEmptyState />
      ) : null}

      {!isEmptyState && visitorId && !isLoading ? (
        <MyAccountRecentReadPopoverList
          articleList={readArticleList.slice(0, 5)}
          isDisplayViewMore={isDisplayViewMore}
        />
      ) : null}

      {isLoading && visitorId ? (
        <div className="text-center">
          <FontAwesomeIcon icon={faSpinner} size="2x" spin className="mb-6" />
        </div>
      ) : null}
    </div>
  );
};

export default MyAccountReadingHistoryPopover;
